import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useLayoutEffect,
  useMemo,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { AgentAssetAttachment } from '@nest-react/domain';
import type { AgentChatUiMessage } from '~/features/studio/AgentChatThread';
import type {
  AgentGalleryFocus,
  AgentGalleryItem,
} from '~/features/studio/AgentChatGallery';
import type { AgentReferenceImage } from '~/features/studio/AgentReferenceImageList';
import type { DirectVideoSetupContext } from '~/features/studio/composer/types';

import {
  type AgentComposerSessionInit,
  type AgentComposerSessionSnapshot,
  buildAgentComposerSessionKey,
  emptyAgentComposerSessionSnapshot,
} from './agentComposerSessionTypes';
import { useWorkspaceStore } from './workspaceStore';

const EMPTY_AGENT_COMPOSER_SESSION = emptyAgentComposerSessionSnapshot();

const resolveSetStateAction = <T>(current: T, action: SetStateAction<T>): T => {
  if (typeof action === 'function') {
    return (action as (value: T) => T)(current);
  }
  return action;
};

export const readAgentComposerSessionSnapshot = (
  key: string | null
): AgentComposerSessionSnapshot => {
  if (!key) return EMPTY_AGENT_COMPOSER_SESSION;
  return (
    useWorkspaceStore.getState().agentComposerByKey[key] ??
    EMPTY_AGENT_COMPOSER_SESSION
  );
};

const readSessionSnapshot = readAgentComposerSessionSnapshot;

type WorkspaceAgentComposerSessionOptions = {
  workspaceId?: string;
  routeSessionId?: string;
  initialSessionLoading?: boolean;
  initialPatch?: Partial<AgentComposerSessionSnapshot>;
};

export const useWorkspaceAgentComposerSession = ({
  workspaceId,
  routeSessionId,
  initialSessionLoading = false,
  initialPatch,
}: WorkspaceAgentComposerSessionOptions): AgentComposerSessionSnapshot & {
  sessionKey: string | null;
  resetSession: (init?: AgentComposerSessionInit) => void;
  patchSession: (patch: Partial<AgentComposerSessionSnapshot>) => void;
  setInput: Dispatch<SetStateAction<string>>;
  setReferenceImages: Dispatch<SetStateAction<AgentReferenceImage[]>>;
  setAssetAttachments: Dispatch<SetStateAction<AgentAssetAttachment[]>>;
  setAssetAttachmentError: Dispatch<SetStateAction<string | null>>;
  setMessages: Dispatch<SetStateAction<AgentChatUiMessage[]>>;
  setActiveSkillId: Dispatch<SetStateAction<string | null>>;
  setDirectVideoSetup: Dispatch<SetStateAction<DirectVideoSetupContext | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setIsStreaming: Dispatch<SetStateAction<boolean>>;
  setIsThinking: Dispatch<SetStateAction<boolean>>;
  setActivePlan: Dispatch<
    SetStateAction<AgentComposerSessionSnapshot['activePlan']>
  >;
  setPlanning: Dispatch<SetStateAction<boolean>>;
  setApprovingPlanStepId: Dispatch<SetStateAction<string | null>>;
  setConfirmingPlanStepId: Dispatch<SetStateAction<string | null>>;
  setRecoveringPlanStepId: Dispatch<SetStateAction<string | null>>;
  setActivePlanExecutions: Dispatch<
    SetStateAction<AgentComposerSessionSnapshot['activePlanExecutions']>
  >;
  setSessionEngaged: Dispatch<SetStateAction<boolean>>;
  setSessionLoading: Dispatch<SetStateAction<boolean>>;
  setPersistedSessionId: Dispatch<SetStateAction<string | undefined>>;
  setLocalDraftSessionId: Dispatch<SetStateAction<string | null>>;
  setSuppressRouteSessionId: Dispatch<SetStateAction<boolean>>;
  setTitleLocked: Dispatch<SetStateAction<boolean>>;
  setPendingFirstInput: Dispatch<SetStateAction<boolean>>;
  setIsHydrating: Dispatch<SetStateAction<boolean>>;
  setHydrationGeneration: Dispatch<SetStateAction<number>>;
  setSkipNextGalleryRefresh: Dispatch<SetStateAction<boolean>>;
  setRemoteGalleryItems: Dispatch<SetStateAction<AgentGalleryItem[]>>;
  setGalleryLoading: Dispatch<SetStateAction<boolean>>;
  setGalleryStageId: Dispatch<SetStateAction<string | null>>;
  setGalleryTab: Dispatch<SetStateAction<'generations' | 'cuts'>>;
  setEditorProjectId: Dispatch<SetStateAction<string>>;
  setEditorOpening: Dispatch<SetStateAction<boolean>>;
  setGalleryFocus: Dispatch<SetStateAction<AgentGalleryFocus | null>>;
  setPendingLaunchToken: Dispatch<SetStateAction<string | null>>;
  setSessionTitle: Dispatch<SetStateAction<string>>;
} => {
  const draftSessionKey = workspaceId
    ? buildAgentComposerSessionKey(workspaceId)
    : null;
  const sessionKey = workspaceId
    ? buildAgentComposerSessionKey(workspaceId, routeSessionId)
    : null;
  const snapshot = useWorkspaceStore(
    useShallow(state => {
      if (!sessionKey) return EMPTY_AGENT_COMPOSER_SESSION;
      const direct = state.agentComposerByKey[sessionKey];
      if (direct) return direct;
      if (routeSessionId && draftSessionKey && sessionKey !== draftSessionKey) {
        const draft = state.agentComposerByKey[draftSessionKey];
        if (
          draft &&
          (draft.persistedSessionId === routeSessionId ||
            draft.localDraftSessionId === routeSessionId)
        ) {
          return draft;
        }
      }
      return EMPTY_AGENT_COMPOSER_SESSION;
    })
  );
  const ensureAgentComposerSession = useWorkspaceStore(
    state => state.ensureAgentComposerSession
  );
  const migrateAgentComposerSession = useWorkspaceStore(
    state => state.migrateAgentComposerSession
  );
  const patchAgentComposerSession = useWorkspaceStore(
    state => state.patchAgentComposerSession
  );
  const resetAgentComposerSession = useWorkspaceStore(
    state => state.resetAgentComposerSession
  );
  const setAgentComposerMessages = useWorkspaceStore(
    state => state.setAgentComposerMessages
  );
  const setAgentComposerReferenceImages = useWorkspaceStore(
    state => state.setAgentComposerReferenceImages
  );

  useLayoutEffect(() => {
    if (!workspaceId || !sessionKey) return;

    const store = useWorkspaceStore.getState();
    const draftSnapshot = draftSessionKey
      ? store.agentComposerByKey[draftSessionKey]
      : undefined;
    const activeSnapshot = store.agentComposerByKey[sessionKey];

    if (
      routeSessionId &&
      draftSessionKey &&
      sessionKey !== draftSessionKey &&
      !activeSnapshot &&
      draftSnapshot &&
      (draftSnapshot.persistedSessionId === routeSessionId ||
        draftSnapshot.localDraftSessionId === routeSessionId)
    ) {
      migrateAgentComposerSession(draftSessionKey, sessionKey);
      return;
    }

    if (!activeSnapshot) {
      ensureAgentComposerSession(sessionKey, {
        sessionLoading: initialSessionLoading,
        patch: initialPatch,
      });
    }
  }, [
    draftSessionKey,
    ensureAgentComposerSession,
    initialPatch,
    initialSessionLoading,
    migrateAgentComposerSession,
    routeSessionId,
    sessionKey,
    workspaceId,
  ]);

  const patchSession = useCallback(
    (patch: Partial<AgentComposerSessionSnapshot>): void => {
      if (!sessionKey) return;
      patchAgentComposerSession(sessionKey, patch);
    },
    [patchAgentComposerSession, sessionKey]
  );

  const resetSession = useCallback(
    (init?: AgentComposerSessionInit): void => {
      if (!sessionKey) return;
      resetAgentComposerSession(sessionKey, init);
    },
    [resetAgentComposerSession, sessionKey]
  );

  const createFieldSetter = useCallback(
    <K extends keyof AgentComposerSessionSnapshot>(
      field: K
    ): Dispatch<SetStateAction<AgentComposerSessionSnapshot[K]>> =>
      action => {
        if (!sessionKey) return;
        const current = readSessionSnapshot(sessionKey);
        patchAgentComposerSession(sessionKey, {
          [field]: resolveSetStateAction(current[field], action),
        });
      },
    [patchAgentComposerSession, sessionKey]
  );

  const setters = useMemo(
    () => ({
      setInput: createFieldSetter('input'),
      setAssetAttachments: createFieldSetter('assetAttachments'),
      setAssetAttachmentError: createFieldSetter('assetAttachmentError'),
      setActiveSkillId: createFieldSetter('activeSkillId'),
      setDirectVideoSetup: createFieldSetter('directVideoSetup'),
      setLoading: createFieldSetter('loading'),
      setIsStreaming: createFieldSetter('isStreaming'),
      setIsThinking: createFieldSetter('isThinking'),
      setActivePlan: createFieldSetter('activePlan'),
      setPlanning: createFieldSetter('planning'),
      setApprovingPlanStepId: createFieldSetter('approvingPlanStepId'),
      setConfirmingPlanStepId: createFieldSetter('confirmingPlanStepId'),
      setRecoveringPlanStepId: createFieldSetter('recoveringPlanStepId'),
      setActivePlanExecutions: createFieldSetter('activePlanExecutions'),
      setSessionEngaged: createFieldSetter('sessionEngaged'),
      setSessionLoading: createFieldSetter('sessionLoading'),
      setPersistedSessionId: createFieldSetter('persistedSessionId'),
      setLocalDraftSessionId: createFieldSetter('localDraftSessionId'),
      setSuppressRouteSessionId: createFieldSetter('suppressRouteSessionId'),
      setTitleLocked: createFieldSetter('titleLocked'),
      setPendingFirstInput: createFieldSetter('pendingFirstInput'),
      setIsHydrating: createFieldSetter('isHydrating'),
      setHydrationGeneration: createFieldSetter('hydrationGeneration'),
      setSkipNextGalleryRefresh: createFieldSetter('skipNextGalleryRefresh'),
      setRemoteGalleryItems: createFieldSetter('remoteGalleryItems'),
      setGalleryLoading: createFieldSetter('galleryLoading'),
      setGalleryStageId: createFieldSetter('galleryStageId'),
      setGalleryTab: createFieldSetter('galleryTab'),
      setEditorProjectId: createFieldSetter('editorProjectId'),
      setEditorOpening: createFieldSetter('editorOpening'),
      setGalleryFocus: createFieldSetter('galleryFocus'),
      setPendingLaunchToken: createFieldSetter('pendingLaunchToken'),
      setSessionTitle: createFieldSetter('sessionTitle'),
      setMessages: ((action: SetStateAction<AgentChatUiMessage[]>) => {
        if (!sessionKey) return;
        setAgentComposerMessages(sessionKey, action);
      }) as Dispatch<SetStateAction<AgentChatUiMessage[]>>,
      setReferenceImages: ((action: SetStateAction<AgentReferenceImage[]>) => {
        if (!sessionKey) return;
        setAgentComposerReferenceImages(sessionKey, action);
      }) as Dispatch<SetStateAction<AgentReferenceImage[]>>,
    }),
    [
      createFieldSetter,
      sessionKey,
      setAgentComposerMessages,
      setAgentComposerReferenceImages,
    ]
  );

  return {
    ...snapshot,
    sessionKey,
    resetSession,
    patchSession,
    ...setters,
  };
};
